import { type ChangeEvent, type FormEvent, useState } from 'react';
import { Link } from 'react-router-dom';
import { ExternalLink, FileText } from 'lucide-react';
import {
  useCompetitionLanding,
  useRemoveCompetitionAnnouncement,
  useUpdateCompetitionLanding,
  useUploadCompetitionAnnouncement,
  useCompetitionIdParam,
} from '@/hooks/useCompetitions';
import { PageSpinner } from '@/components/ui/Spinner';
import { Alert, ErrorState } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { Card, CardBody, CardHeader } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { getApiErrorMessage } from '@/api/client';
import type { CompetitionLanding, ImageRequirements } from '@/api/types';
import { IMAGE_FORMATS } from '@/lib/imageRequirements';

/** "" -> null, "3000" -> 3000; anything that is not a positive whole number is treated as not set. */
function toNumber(value: string): number | null {
  const n = Number.parseInt(value, 10);
  return Number.isFinite(n) && n > 0 ? n : null;
}

const asText = (n: number | null) => (n == null ? '' : String(n));

/** The razpis itself is a PDF document; everything else on the public page is the competition description. */
function AnnouncementCard({ landing }: { landing: CompetitionLanding }) {
  const upload = useUploadCompetitionAnnouncement(landing.id);
  const remove = useRemoveCompetitionAnnouncement(landing.id);

  async function handleFile(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    await upload.mutateAsync(file);
  }

  return (
    <Card>
      <CardHeader>
        <h2 className="font-semibold text-fg">Razpis (PDF)</h2>
        <p className="text-sm text-fg-muted">
          Naložite razpis kot PDF dokument (do 20 MB). Na javni strani je pod opisom tekmovanja, tekmovalci ga lahko
          odprejo ali prenesejo. Pravila, nagrade, žirijo in kontakt napišite v razpis.
        </p>
      </CardHeader>
      <CardBody className="flex flex-col gap-3">
        {upload.isError && <Alert variant="error">{getApiErrorMessage(upload.error)}</Alert>}
        {remove.isError && <Alert variant="error">{getApiErrorMessage(remove.error)}</Alert>}
        {landing.announcementUrl ? (
          <div className="flex flex-wrap items-center gap-3">
            <a
              href={landing.announcementUrl}
              target="_blank"
              rel="noreferrer"
              className="flex items-center gap-2 text-sm font-medium text-accent-text underline"
            >
              <FileText size={16} aria-hidden="true" />
              {landing.announcementFileName ?? 'razpis.pdf'}
            </a>
            <Button
              type="button"
              variant="danger"
              size="sm"
              isLoading={remove.isPending}
              onClick={() => {
                if (confirm('Odstranim razpis (PDF)?')) remove.mutate();
              }}
            >
              Odstrani
            </Button>
          </div>
        ) : (
          <p className="text-sm text-fg-muted">Razpis še ni naložen.</p>
        )}
        <label className="self-start">
          <input type="file" accept="application/pdf,.pdf" className="sr-only" onChange={handleFile} disabled={upload.isPending} />
          <span className="inline-flex cursor-pointer items-center rounded-control border border-line bg-surface-1 px-3 py-1.5 text-sm font-medium text-fg hover:bg-surface-2">
            {upload.isPending ? 'Nalaganje …' : landing.announcementUrl ? 'Zamenjaj PDF' : 'Naloži PDF'}
          </span>
        </label>
      </CardBody>
    </Card>
  );
}

function LandingForm({ landing }: { landing: CompetitionLanding }) {
  const update = useUpdateCompetitionLanding(landing.id);
  const req = landing.imageRequirements;

  const [formats, setFormats] = useState<string[]>(req.allowedFormats);
  const [maxFileSizeMb, setMaxFileSizeMb] = useState(asText(req.maxFileSizeMb));
  const [minLongSide, setMinLongSide] = useState(asText(req.minLongSidePx));
  const [maxLongSide, setMaxLongSide] = useState(asText(req.maxLongSidePx));
  const [minDpi, setMinDpi] = useState(asText(req.minDpi));
  const [requireExif, setRequireExif] = useState(req.requireExif);
  const [rejectNonCompliant, setRejectNonCompliant] = useState(req.rejectNonCompliant);
  const [saved, setSaved] = useState(false);

  function toggleFormat(value: string) {
    setFormats((current) => (current.includes(value) ? current.filter((f) => f !== value) : [...current, value]));
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setSaved(false);
    const imageRequirements: ImageRequirements = {
      allowedFormats: formats,
      maxFileSizeMb: toNumber(maxFileSizeMb),
      minLongSidePx: toNumber(minLongSide),
      maxLongSidePx: toNumber(maxLongSide),
      minDpi: toNumber(minDpi),
      requireExif,
      rejectNonCompliant,
    };
    await update.mutateAsync({
      imageRequirements,
    });
    setSaved(true);
  }

  const markDirty = () => setSaved(false);

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-6" onChange={markDirty}>
      {update.isError && <Alert variant="error">{getApiErrorMessage(update.error)}</Alert>}
      {saved && <Alert variant="success">Razpis je shranjen.</Alert>}

      <Card>
        <CardHeader>
          <h2 className="font-semibold text-fg">Zahteve za fotografije</h2>
          <p className="text-sm text-fg-muted">
            Tekmovalci jih vidijo na javni strani in pred nalaganjem. Format in velikost preverimo ob nalaganju, dolžino
            stranice, ločljivost in podatke EXIF pa takoj po njem. Prazno polje pomeni brez omejitve.
          </p>
        </CardHeader>
        <CardBody className="flex flex-col gap-5">
          <fieldset className="flex flex-col gap-2">
            <legend className="text-sm font-medium text-fg">Dovoljeni formati</legend>
            <div className="flex flex-wrap gap-4">
              {IMAGE_FORMATS.map((format) => (
                <label key={format.value} className="flex items-center gap-2 text-sm text-fg-secondary">
                  <input
                    type="checkbox"
                    checked={formats.includes(format.value)}
                    onChange={() => toggleFormat(format.value)}
                    className="h-4 w-4 accent-accent"
                  />
                  {format.label}
                </label>
              ))}
            </div>
            <span className="text-xs text-fg-muted">Če ni izbran noben format, so dovoljeni vsi (JPG, PNG, WebP).</span>
          </fieldset>

          <div className="grid gap-4 sm:grid-cols-2">
            <Input
              label="Največja velikost datoteke (MB)"
              type="number"
              min={1}
              max={50}
              value={maxFileSizeMb}
              onChange={(e) => setMaxFileSizeMb(e.target.value)}
              placeholder="Na primer 3"
            />
            <Input
              label="Najmanjša ločljivost (dpi)"
              type="number"
              min={1}
              max={2400}
              value={minDpi}
              onChange={(e) => setMinDpi(e.target.value)}
              placeholder="Na primer 300"
            />
            <Input
              label="Daljša stranica, najmanj (px)"
              type="number"
              min={100}
              max={20000}
              value={minLongSide}
              onChange={(e) => setMinLongSide(e.target.value)}
              placeholder="Na primer 3000"
            />
            <Input
              label="Daljša stranica, največ (px)"
              type="number"
              min={100}
              max={20000}
              value={maxLongSide}
              onChange={(e) => setMaxLongSide(e.target.value)}
              placeholder="Na primer 3000"
            />
          </div>
          <p className="-mt-3 text-xs text-fg-muted">
            Če mora imeti daljša stranica natančno določeno velikost, v obe polji vpišite isto število.
          </p>

          <label className="flex items-center gap-2 text-sm text-fg-secondary">
            <input
              type="checkbox"
              checked={requireExif}
              onChange={(e) => setRequireExif(e.target.checked)}
              className="h-4 w-4 accent-accent"
            />
            Fotografija mora vsebovati podatke EXIF
          </label>

          <fieldset className="flex flex-col gap-2 border-t border-line pt-4">
            <legend className="text-sm font-medium text-fg">Slika, ki ne ustreza zahtevam</legend>
            <label className="flex items-start gap-2 text-sm text-fg-secondary">
              <input
                type="radio"
                name="nonCompliant"
                checked={rejectNonCompliant}
                onChange={() => setRejectNonCompliant(true)}
                className="mt-0.5 h-4 w-4 accent-accent"
              />
              <span>
                <span className="font-medium text-fg">Zavrni takoj.</span> Slike z napakami tekmovalec ne more oddati;
                pove mu, kaj mora popraviti.
              </span>
            </label>
            <label className="flex items-start gap-2 text-sm text-fg-secondary">
              <input
                type="radio"
                name="nonCompliant"
                checked={!rejectNonCompliant}
                onChange={() => setRejectNonCompliant(false)}
                className="mt-0.5 h-4 w-4 accent-accent"
              />
              <span>
                <span className="font-medium text-fg">Sprejmi in označi.</span> Slika ostane, prispevek pa dobi
                opozorilo, da ne ustreza zahtevam. Organizator ga lahko kasneje zavrne.
              </span>
            </label>
          </fieldset>
        </CardBody>
      </Card>

      <Button type="submit" isLoading={update.isPending} className="self-start">
        Shrani razpis
      </Button>
    </form>
  );
}

export function OrganizerLandingPage({ embedded = false }: { embedded?: boolean } = {}) {
  const { competitionId } = useCompetitionIdParam();
  const landing = useCompetitionLanding(competitionId || undefined);

  if (landing.isLoading) return <PageSpinner />;
  if (landing.isError || !landing.data) {
    return <ErrorState message={getApiErrorMessage(landing.error)} onRetry={() => landing.refetch()} />;
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          {!embedded && (
            <>
          <Link to="/organizer/competitions" className="text-sm text-fg-muted hover:underline">
            ← Upravljanje tekmovanj
          </Link>
          <h1 className="text-xl font-semibold text-fg">Razpis: {landing.data.name}</h1>
            </>
          )}
          <p className="text-sm text-fg-muted">
            Javna stran tekmovanja: opis tekmovanja, razpis kot PDF in zahteve za fotografije. Opis in datume urejate
            v nastavitvah tekmovanja (korak »Osnovno«).
          </p>
        </div>
        <Link to={`/razpis/${landing.data.slug}`} target="_blank" rel="noreferrer">
          <Button variant="secondary" size="sm">
            <ExternalLink size={14} className="mr-1.5" aria-hidden="true" />
            Ogled javne strani
          </Button>
        </Link>
      </div>
      <AnnouncementCard landing={landing.data} />
      <LandingForm landing={landing.data} />
    </div>
  );
}
